import { ComputedOrImmediate, ts } from '@funkey/shared';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { useI18n } from '~/features/i18n';
import { useBoolean } from '~/utils/react';

export function useClipboard_CopySDK(
    value: ComputedOrImmediate<() => string>,
    options?: {
        onCopy?(): void;
    },
) {
    const i18n = useI18n();
    const isCopied = useBoolean(false);

    return useMemo(
        () => ({
            isCopied: isCopied.state,

            async onCopy() {
                try {
                    await navigator.clipboard.writeText(ComputedOrImmediate.get(value));
                    isCopied.setTrue();
                    window.setTimeout(() => isCopied.setFalse(), 2000);
                    toast.success(i18n._('clipboard.toast.success'));
                    options?.onCopy?.();
                } catch {
                    toast.error(i18n._('clipboard.toast.error'));
                }
            },
        }),
        [isCopied, value, options?.onCopy, i18n],
    );
}

export declare namespace Clipboard_Copy {
    export type Props = {
        value: ComputedOrImmediate<() => string>;
        /** Callback after being copied */
        onCopy?(): void;
        children: React.FC<{
            isCopied: boolean;
            /** On-click copy action */
            onClick: React.MouseEventHandler;
            /** Copy action */
            onCopy: () => Promise<void>;
        }>;
    };
}
export const Clipboard_Copy: React.FC<Clipboard_Copy.Props> = props => {
    const sdk = useClipboard_CopySDK(props.value, { onCopy: props.onCopy });

    return (
        <props.children
            isCopied={sdk.isCopied}
            onCopy={sdk.onCopy}
            onClick={async e => {
                if (e.isDefaultPrevented()) return;
                await sdk.onCopy();
            }}
        />
    );
};

export const Clipboard_Copy_Button: React.FC<
    ts.Object.override<
        React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
        Omit<Clipboard_Copy.Props, 'children'> & {
            children: ComputedOrImmediate<(isCopied: boolean) => React.ReactNode>;
        }
    >
> = ({ value, children, onCopy, ...props }) => (
    <Clipboard_Copy value={value} onCopy={onCopy}>
        {renderProps => (
            <button
                {...props}
                type="button"
                onClick={async e => {
                    await (props.onClick?.(e) as any);
                    return renderProps.onClick(e);
                }}
            >
                {ComputedOrImmediate.get(children, renderProps.isCopied)}
            </button>
        )}
    </Clipboard_Copy>
);
